import { AnimatePresence, motion } from "framer-motion";
import cls from "./styles.module.css";
import type { TPostPhoto } from "@/network/typings";
import { useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { preloadImage } from "@/utils/preload-image";

export type TProps = {
  thumbnail: TPostPhoto;
  photos: TPostPhoto[];
  postLink: string;
};
type TDirection = -1 | 1;

export default function PostThumb({
  thumbnail: thumb,
  photos: basePhotos,
  postLink,
}: TProps) {
  const isDraggingRef = useRef(false);
  const isPhotosPreloaded = useRef(false);
  const directionRef = useRef<TDirection>(1);
  const [activeIdx, setActiveIdx] = useState(0);
  const photos = useMemo((): TPostPhoto[] => {
    return [thumb, ...basePhotos];
  }, [thumb, basePhotos]);
  const activePhoto = photos[activeIdx];
  const goNext = () => {
    directionRef.current = 1;
    setActiveIdx((idx) => (idx >= photos.length - 1 ? 0 : idx + 1));
  };
  const goPrev = () => {
    directionRef.current = -1;
    setActiveIdx((idx) => (idx === 0 ? photos.length - 1 : idx - 1));
  };
  const maybePreloadPhotos = () => {
    if (isPhotosPreloaded.current) return;
    for (const photo of basePhotos) {
      preloadImage(photo.url);
    }
    isPhotosPreloaded.current = true;
  };
  return (
    <Link
      draggable="false"
      to={postLink}
      onClick={(e) => {
        if (isDraggingRef.current) {
          e.preventDefault();
        }
      }}
    >
      <div
        style={{
          aspectRatio: `${thumb.width} / ${thumb.height}`,
        }}
        className={cls.root}
      >
        <AnimatePresence initial={false}>
          {activePhoto && (
            <motion.img
              loading="lazy"
              transition={{
                ease: "easeInOut",
                duration: 0.3,
              }}
              variants={{
                enter: () => {
                  return {
                    x: directionRef.current === 1 ? "100%" : "-100%",
                    opacity: 0,
                  };
                },
                center: {
                  zIndex: 1,
                  x: 0,
                  opacity: 1,
                },
                exit: () => {
                  return {
                    zIndex: 0,
                    x: directionRef.current === -1 ? "100%" : "-100%",
                    opacity: 0,
                  };
                },
              }}
              onDragStart={() => {
                isDraggingRef.current = true;
                maybePreloadPhotos();
              }}
              onDragEnd={(_e, { offset, velocity }) => {
                isDraggingRef.current = false;
                const swipeConfidenceThreshold = 10000;
                const swipe = Math.abs(offset.x) * velocity.x;
                if (swipe < -swipeConfidenceThreshold) {
                  goNext();
                } else if (swipe > swipeConfidenceThreshold) {
                  goPrev();
                }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.5}
              key={activeIdx}
              initial="enter"
              animate="center"
              exit="exit"
              alt={activePhoto.altText}
              width={activePhoto.width}
              height={activePhoto.height}
              src={activePhoto.url}
              // we preload src on drag start so there's no point of using srcset for other images
              srcSet={activeIdx === 0 ? activePhoto.srcset : undefined}
            />
          )}
        </AnimatePresence>
      </div>
    </Link>
  );
}
